import { apiUrl, authHeaders } from './auth';

// Document management (HHLTB-2808) — the flyers, handouts, forms and letters
// that used to be editable only in legacy.

export interface DocumentCategory {
    id: number;
    name: string;
    label: string;
    client_visible: boolean;
}

export interface MedicalReasonBrief {
    id: number;
    name: string;
}

export interface StandardDocument {
    id: number;
    name: string;
    doc_type: string;
    category: DocumentCategory | null;
    medical_reasons: MedicalReasonBrief[];
    language: string;
    description: string;
    sequence: number | null;
    is_active: boolean;
    document: string;
    url: string;
    file_name: string;
    has_file: boolean;
    // True when the document can never reach a client dashboard, because the
    // materials panel only reads the client-visible categories.
    needs_category: boolean;
    created_date: string;
    created_by_name: string;
    update_date: string;
    updated_by_name: string;
}

export interface DocumentMetadata {
    doc_types: { value: string; label: string }[];
    categories: DocumentCategory[];
    languages: { value: string; label: string }[];
    medical_reasons: MedicalReasonBrief[];
}

export interface DocumentPayload {
    name: string;
    doc_type: string;
    category?: number | null;
    medical_reasons?: number[];
    language: string;
    description?: string;
    is_active?: boolean;
    document?: string;
}

// Surface the API's field errors instead of a generic message, so the form can
// show "a category is required" next to the field that caused it.
const parseError = async (resp: Response, fallback: string) => {
    let data: any = null;
    try {
        data = await resp.json();
    } catch {
        // non-JSON body; fall through to the generic message
    }
    const error: any = new Error(data?.detail || fallback);
    error.data = data;
    return error;
};

export const fetchDocuments = async (params: {
    docType?: string;
    includeInactive?: boolean;
    search?: string;
    language?: string;
    // A category id, or 'none' for the documents with no category at all.
    category?: string;
} = {}): Promise<StandardDocument[]> => {
    const headers = authHeaders();
    const qs = new URLSearchParams();
    if (params.docType) qs.append('doc_type', params.docType);
    if (params.includeInactive) qs.append('include_inactive', 'true');
    if (params.search) qs.append('search', params.search);
    if (params.language) qs.append('language', params.language);
    if (params.category) qs.append('category', params.category);

    const resp = await fetch(`${apiUrl}/admin/documents?${qs.toString()}`, {
        method: 'get',
        headers,
    });
    if (!resp.ok) throw await parseError(resp, 'Error fetching documents');
    return await resp.json();
};

export const fetchDocumentMetadata = async (): Promise<DocumentMetadata> => {
    const headers = authHeaders();
    const resp = await fetch(`${apiUrl}/admin/documents/metadata`, {
        method: 'get',
        headers,
    });
    if (!resp.ok) throw await parseError(resp, 'Error fetching document options');
    return await resp.json();
};

export const createDocument = async (
    payload: DocumentPayload,
): Promise<StandardDocument> => {
    const headers = authHeaders();
    const resp = await fetch(`${apiUrl}/admin/documents`, {
        method: 'post',
        headers,
        body: JSON.stringify(payload),
    });
    if (!resp.ok) throw await parseError(resp, 'Error creating document');
    return await resp.json();
};

export const updateDocument = async (
    id: number,
    payload: Partial<DocumentPayload>,
): Promise<StandardDocument> => {
    const headers = authHeaders();
    const resp = await fetch(`${apiUrl}/admin/documents/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(payload),
    });
    if (!resp.ok) throw await parseError(resp, 'Error updating document');
    return await resp.json();
};

// Deactivates rather than removing the row: the document may be a letter
// template or be linked from a client dashboard, and the file stays in S3.
export const deactivateDocument = async (id: number): Promise<StandardDocument> => {
    const headers = authHeaders();
    const resp = await fetch(`${apiUrl}/admin/documents/${id}`, {
        method: 'DELETE',
        headers,
    });
    if (!resp.ok) throw await parseError(resp, 'Error deactivating document');
    return await resp.json();
};

export const reactivateDocument = async (id: number): Promise<StandardDocument> => {
    const headers = authHeaders();
    const resp = await fetch(`${apiUrl}/admin/documents/${id}/reactivate`, {
        method: 'post',
        headers,
    });
    if (!resp.ok) throw await parseError(resp, 'Error reactivating document');
    return await resp.json();
};

export const moveDocument = async (
    id: number,
    direction: 'up' | 'down',
): Promise<StandardDocument> => {
    const headers = authHeaders();
    const resp = await fetch(`${apiUrl}/admin/documents/${id}/move`, {
        method: 'post',
        headers,
        body: JSON.stringify({ direction }),
    });
    if (!resp.ok) throw await parseError(resp, 'Error reordering document');
    return await resp.json();
};

/**
 * Upload a file straight to S3 with a presigned POST, the same way campaign
 * photos and fund request attachments go up, and return the object key to
 * save on the document. The file never passes through the API.
 */
export const uploadDocumentFile = async (file: File): Promise<string> => {
    const headers = authHeaders();
    const presignResp = await fetch(
        `${apiUrl}/admin/documents/presignedUrl?filename=${encodeURIComponent(file.name)}`,
        { method: 'get', headers },
    );
    if (!presignResp.ok) {
        throw await parseError(presignResp, 'Could not prepare the file upload');
    }
    const presigned = await presignResp.json();

    const formData = new FormData();
    Object.entries(presigned.fields || {}).forEach(([key, value]) => {
        formData.append(key, value as string);
    });
    formData.append('file', file);

    // No auth headers here on purpose: the presigned fields are the
    // authorization, and S3 rejects the request if we add our own.
    const uploadResp = await fetch(presigned.url, {
        method: 'post',
        body: formData,
    });
    if (!uploadResp.ok) {
        throw new Error('The file could not be uploaded to storage');
    }

    return presigned.key;
};
